import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Shield, Eye, EyeOff, FileText } from "lucide-react";

export default function CompanyInterviewerSettings() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [data, setData] = useState({ default: { resume_type: "masked", ai_visible: false }, overrides: [] });
    const [interviewers, setInterviewers] = useState([]);

    const load = async () => {
        setLoading(true);
        try {
            const [{ data: v }, { data: ivs }] = await Promise.all([
                api.get("/company/interviewer-visibility"),
                api.get("/company/interviewers"),
            ]);
            setData(v);
            setInterviewers(ivs || []);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const updateDefault = async (patch) => {
        setSaving(true);
        try {
            const { data: d } = await api.patch("/company/interviewer-visibility", patch);
            setData((prev) => ({ ...prev, default: { resume_type: d.resume_type, ai_visible: !!d.ai_visible } }));
            toast.success("Company default updated");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const upsertOverride = async (iid, patch) => {
        try {
            await api.patch(`/company/interviewer-visibility/${iid}`, patch);
            toast.success("Override saved");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const removeOverride = async (iid) => {
        try {
            await api.delete(`/company/interviewer-visibility/${iid}`);
            toast.success("Override removed");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const overrideFor = (iid) => data.overrides.find((o) => o.interviewer_id === iid);

    if (loading) {
        return <div className="p-8 text-muted-foreground font-mono text-xs">Loading…</div>;
    }

    return (
        <div className="max-w-4xl mx-auto px-4 py-6 space-y-6" data-testid="iv-settings-page">
            <div>
                <div className="text-xs uppercase tracking-widest font-mono text-ikb">Admin settings</div>
                <h1 className="font-display font-black text-2xl sm:text-4xl mt-2 flex items-center gap-2">
                    <Shield className="w-7 h-7 text-ikb" /> Interviewer visibility
                </h1>
                <p className="text-xs text-muted-foreground font-mono mt-1">
                    Set what an interviewer sees — the <strong>original</strong> resume or the <strong>masked</strong> version, and whether AI analysis is unlocked. Company default applies to everyone; override per interviewer when needed.
                </p>
            </div>

            {/* Company default */}
            <section className="border border-border bg-white p-5 space-y-4" data-testid="iv-settings-default">
                <h2 className="font-display font-black text-lg">Company default</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 mb-1 flex items-center gap-1.5">
                            <FileText className="w-3 h-3" /> Resume
                        </div>
                        <div className="inline-flex border border-border bg-white w-full">
                            <button onClick={() => updateDefault({ resume_type: "masked" })} disabled={saving} className={`flex-1 px-3 py-2 text-xs font-mono uppercase tracking-widest ${data.default.resume_type === "masked" ? "bg-ikb text-white" : "hover:bg-secondary"}`} data-testid="iv-default-masked">Masked</button>
                            <button onClick={() => updateDefault({ resume_type: "original" })} disabled={saving} className={`flex-1 px-3 py-2 text-xs font-mono uppercase tracking-widest border-l border-border ${data.default.resume_type === "original" ? "bg-ikb text-white" : "hover:bg-secondary"}`} data-testid="iv-default-original">Original</button>
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 mb-1 flex items-center gap-1.5">
                            {data.default.ai_visible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />} AI analysis
                        </div>
                        <div className="inline-flex border border-border bg-white w-full">
                            <button onClick={() => updateDefault({ ai_visible: false })} disabled={saving} className={`flex-1 px-3 py-2 text-xs font-mono uppercase tracking-widest ${!data.default.ai_visible ? "bg-ikb text-white" : "hover:bg-secondary"}`} data-testid="iv-default-ai-off">Hidden</button>
                            <button onClick={() => updateDefault({ ai_visible: true })} disabled={saving} className={`flex-1 px-3 py-2 text-xs font-mono uppercase tracking-widest border-l border-border ${data.default.ai_visible ? "bg-ikb text-white" : "hover:bg-secondary"}`} data-testid="iv-default-ai-on">Visible</button>
                        </div>
                    </div>
                </div>
                <p className="text-[11px] font-mono text-muted-foreground">Default applies to every interviewer in the company unless overridden below.</p>
            </section>

            {/* Per-interviewer overrides */}
            <section className="border border-border bg-white p-5" data-testid="iv-settings-overrides">
                <h2 className="font-display font-black text-lg mb-3">Per-interviewer overrides</h2>
                {interviewers.length === 0 ? (
                    <div className="text-xs text-muted-foreground font-mono">No interviewers added yet. Invite interviewers from <a href="/company/interviewers" className="text-ikb underline">Company → Interviewers</a>.</div>
                ) : (
                    <div className="divide-y divide-border">
                        {interviewers.map((iv) => {
                            const o = overrideFor(iv.id);
                            const effResume = o?.resume_type || data.default.resume_type;
                            const effAI = typeof o?.ai_visible === "boolean" ? o.ai_visible : data.default.ai_visible;
                            return (
                                <div key={iv.id} className="py-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`iv-row-${iv.id}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-mono text-sm truncate">{iv.name || iv.email}</div>
                                        <div className="text-[11px] font-mono text-muted-foreground truncate">{iv.email}</div>
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <div className="inline-flex border border-border">
                                            <button onClick={() => upsertOverride(iv.id, { resume_type: "masked" })} className={`px-2 py-1 text-[10px] font-mono uppercase tracking-widest ${effResume === "masked" ? "bg-ikb text-white" : "hover:bg-secondary"}`} data-testid={`iv-row-${iv.id}-masked`}>Masked</button>
                                            <button onClick={() => upsertOverride(iv.id, { resume_type: "original" })} className={`px-2 py-1 text-[10px] font-mono uppercase tracking-widest border-l border-border ${effResume === "original" ? "bg-ikb text-white" : "hover:bg-secondary"}`} data-testid={`iv-row-${iv.id}-original`}>Original</button>
                                        </div>
                                        <div className="inline-flex border border-border">
                                            <button onClick={() => upsertOverride(iv.id, { ai_visible: false })} className={`px-2 py-1 text-[10px] font-mono uppercase tracking-widest ${!effAI ? "bg-ikb text-white" : "hover:bg-secondary"}`} data-testid={`iv-row-${iv.id}-ai-off`}>AI off</button>
                                            <button onClick={() => upsertOverride(iv.id, { ai_visible: true })} className={`px-2 py-1 text-[10px] font-mono uppercase tracking-widest border-l border-border ${effAI ? "bg-ikb text-white" : "hover:bg-secondary"}`} data-testid={`iv-row-${iv.id}-ai-on`}>AI on</button>
                                        </div>
                                        {o && (
                                            <Button variant="outline" size="sm" className="rounded-none h-7 text-[10px] text-rose-700 border-rose-300" onClick={() => removeOverride(iv.id)} data-testid={`iv-row-${iv.id}-reset`}>
                                                Reset to default
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>
        </div>
    );
}
